import { REALTIME_SUBSCRIBE_STATES } from "@supabase/supabase-js";
import type { StateCreator } from "zustand";
import { FRAME_PER_SECOND } from "@/lib/constants";
import { supabase } from "@/lib/supabase-client";
import type { ClientStatus } from "@/lib/types";

const DEFAULT_CHANNEL_NAME = "default";

export type ClientStatusSlice = {
  clientStatusReady: boolean;
  clientStatusChannel: string;
  setClientStatusChannel: (channelName?: string) => void;
  clientStatus: ClientStatus;
  setClientStatus: (fn: (status: ClientStatus) => ClientStatus) => void;
  clientStatusDirty: boolean;
  clientStatuses: Record<string, ClientStatus>;
  clientStatusesSubscribe: (
    channelName: string,
    podNumber: string | null,
  ) => () => void;
};

export const createClientStatusSlice: StateCreator<
  ClientStatusSlice,
  [],
  [],
  ClientStatusSlice
> = (set, get) => ({
  clientStatusReady: false,
  clientStatusChannel: DEFAULT_CHANNEL_NAME,
  setClientStatusChannel: (channelName?: string) => {
    set({ clientStatusChannel: channelName ?? DEFAULT_CHANNEL_NAME });
  },
  clientStatus: {},
  setClientStatus: (fn: (status: ClientStatus) => ClientStatus) => {
    set((s) => ({ clientStatus: fn(s.clientStatus), clientStatusDirty: true }));
  },
  clientStatusDirty: false,
  clientStatuses: {},
  clientStatusesSubscribe: (channelName: string, podNumber: string | null) => {
    const channel = supabase.channel(channelName);

    channel
      .on("presence", { event: "sync" }, () => {
        const presenceState = channel.presenceState();
        const podNumbers = Object.values(presenceState)
          .flat()
          .map((p) => (p as { podNumber?: string }).podNumber)
          .filter((p): p is string => !!p);
        set((s) => {
          const next: Record<string, ClientStatus> = {};
          for (const pn of podNumbers) {
            const existing = s.clientStatuses[pn];
            if (existing !== undefined) {
              next[pn] = existing;
            }
          }
          return { clientStatuses: next, clientStatusReady: true };
        });
      })
      .on("broadcast", { event: "status" }, ({ payload }) => {
        const { id, ...newStatus } = payload;
        if (id !== podNumber) {
          set((s) => ({
            clientStatuses: {
              ...s.clientStatuses,
              [id]: newStatus,
            },
          }));
        }
      })
      .subscribe(async (status) => {
        if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
          if (podNumber) {
            await channel.track({ podNumber });
          }
        }
      });

    const interval = setInterval(() => {
      const { clientStatus, clientStatusDirty } = get();
      if (podNumber && clientStatusDirty) {
        set({ clientStatusDirty: false });
        channel.send({
          type: "broadcast",
          event: "status",
          payload: {
            id: podNumber,
            ...clientStatus,
          },
        });
      }
    }, 1000 / FRAME_PER_SECOND);

    return () => {
      clearInterval(interval);
      channel.untrack();
      supabase.removeChannel(channel);
    };
  },
});
